import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../../consts";
import { type ConversionRateCardState, ConversionRateCardShell } from "../../shared/ConversionRateCardShell";

interface IConversionRateMetricCardProps {
    title: string;
    tooltip: string;
    dateRangeText: string;
    countryCode: number;
    countryText: string;
    state: ConversionRateCardState;
    value?: number;
    change?: number | null;
    formatValue: (value: number) => string;
    formatChange: (change: number) => string;
    automationId: string;
}

export const ConversionRateMetricCard = ({
    title,
    tooltip,
    dateRangeText,
    countryCode,
    countryText,
    state,
    value,
    change,
    formatValue,
    formatChange,
    automationId,
}: IConversionRateMetricCardProps) => {
    const { translate } = useTranslate();

    if (state === "available" && value == null) {
        return null;
    }

    const hasChange = change != null;

    return (
        <ConversionRateCardShell
            title={title}
            tooltip={tooltip}
            dateRangeText={dateRangeText}
            countryCode={countryCode}
            countryText={countryText}
            state={state}
            cardAutomationId={automationId}
            lockedCtaAutomationId={`${automationId}-locked-cta`}
        >
            {value != null ? (
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    padding="16px 24px"
                    gap="8px"
                    flex="1"
                >
                    <Text
                        fontVariant="h2"
                        color={colorsPalettes.carbon[500]}
                        data-automation={`${automationId}-value`}
                    >
                        {formatValue(value)}
                    </Text>
                    <Flex
                        gap="4px"
                        alignItems="center"
                    >
                        {hasChange ? (
                            <ChangeValue
                                value={formatChange(change)}
                                isDecrease={change < 0}
                                unsigned={change === 0}
                            />
                        ) : null}
                        <Text
                            fontVariant="body1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {translate(
                                hasChange
                                    ? CONVERSION_RATE_TRANSLATION_KEYS.changeText
                                    : CONVERSION_RATE_TRANSLATION_KEYS.changeNA,
                            )}
                        </Text>
                    </Flex>
                </Flex>
            ) : null}
        </ConversionRateCardShell>
    );
};
